/**
 * Empfänger verwalten.
 *
 * Der Import ist die Stelle, an der die Datenqualität entschieden wird - und
 * damit, ob ein Newsletter persönlich wirkt. Deshalb führt der Assistent
 * durch drei Fragen statt ein nacktes Dateifeld zu zeigen, und er sagt
 * hinterher genau, was er verstanden hat und was nicht.
 */

import { Kontakte, Testmodus, Segmente } from '../api.js';
import {
  el,
  ersetzen,
  karte,
  knopf,
  kennzeichen,
  meldung,
  tabelle,
  dialog,
  eingabefeld,
  laedt,
  zeitpunkt,
} from '../ui.js';

const ANREDE_TEXT = {
  herr: 'Herr',
  frau: 'Frau',
  firma: 'Firma',
  neutral: 'neutral',
};

/**
 * Auswahl für das Anrede-Feld im Formular. Der leere Wert steht bewusst an
 * erster Stelle und ist der Ausgangszustand — es wird nie ein Geschlecht aus
 * dem Vornamen vermutet, das muss immer eine bewusste Angabe sein.
 */
const ANREDE_AUSWAHL = [
  { wert: '', text: '(keine Angabe)' },
  { wert: 'herr', text: 'Herr' },
  { wert: 'frau', text: 'Frau' },
  { wert: 'firma', text: 'Firma' },
  { wert: 'neutral', text: 'neutral' },
];

/**
 * Baut die Anredevorschau, wie sie später in der Mail steht.
 * @param {any} kontakt
 * @returns {string}
 */
function anredeVorschau(kontakt) {
  if (!kontakt.anredeForm || !kontakt.nachname || kontakt.anredeForm === 'firma') {
    return 'Sehr geehrte Damen und Herren';
  }
  const einleitung = kontakt.anredeForm === 'frau' ? 'Sehr geehrte Frau' : 'Sehr geehrter Herr';
  return [einleitung, kontakt.titelVor, kontakt.nachname].filter(Boolean).join(' ');
}

/**
 * Formular zum Anlegen oder Bearbeiten eines einzelnen Empfängers.
 *
 * Dieselbe Maske für beide Fälle: die Felder sind identisch, nur die
 * Beschriftung und was nach dem Speichern passiert, unterscheiden sich.
 *
 * @param {object} optionen
 * @param {any} [optionen.kontakt] Vorhandener Kontakt beim Bearbeiten, sonst leer.
 * @param {(email?: string) => Promise<void>} optionen.nachSpeichern
 *   Wird nach einem erfolgreichen Speichern aufgerufen — beim Neuanlegen mit
 *   der E-Mail-Adresse, damit die aufrufende Stelle den neuen Eintrag in der
 *   Liste sichtbar machen kann (bei 920 Empfängern liegt er sonst außerhalb
 *   der angezeigten ersten Seite).
 */
function empfaengerFormular({ kontakt, nachSpeichern }) {
  const bearbeitenModus = Boolean(kontakt?.id);

  const { feld: emailFeld, eingabe: emailEingabe } = eingabefeld({
    beschriftung: 'E-Mail-Adresse',
    art: 'email',
    wert: kontakt?.email ?? '',
    platzhalter: 'name@beispiel.at',
  });

  const { feld: anredeFeld, eingabe: anredeEingabe } = eingabefeld({
    beschriftung: 'Anrede',
    art: 'select',
    auswahl: ANREDE_AUSWAHL,
    wert: kontakt?.anredeForm ?? '',
    hinweis:
      'Ohne Angabe erscheint in der Mail die neutrale Anrede „Sehr geehrte Damen und Herren". Es wird kein Geschlecht aus dem Vornamen vermutet — das muss immer bewusst gewählt werden.',
  });

  const { feld: titelVorFeld, eingabe: titelVorEingabe } = eingabefeld({
    beschriftung: 'Titel (vorangestellt)',
    wert: kontakt?.titelVor ?? '',
    platzhalter: 'z. B. Mag.',
  });

  const { feld: vornameFeld, eingabe: vornameEingabe } = eingabefeld({
    beschriftung: 'Vorname',
    wert: kontakt?.vorname ?? '',
  });

  const { feld: nachnameFeld, eingabe: nachnameEingabe } = eingabefeld({
    beschriftung: 'Nachname',
    wert: kontakt?.nachname ?? '',
    hinweis: 'Wird für die persönliche Anrede in der Mail gebraucht.',
  });

  const { feld: titelNachFeld, eingabe: titelNachEingabe } = eingabefeld({
    beschriftung: 'Titel (nachgestellt)',
    wert: kontakt?.titelNach ?? '',
    platzhalter: 'z. B. BSc',
  });

  const { feld: firmaFeld, eingabe: firmaEingabe } = eingabefeld({
    beschriftung: 'Firma',
    wert: kontakt?.firma ?? '',
  });

  const { feld: einwilligungFeld, eingabe: einwilligungEingabe } = eingabefeld({
    beschriftung: 'Auf welcher Grundlage darf diese Person angeschrieben werden?',
    platzhalter: 'z. B. Erstgespräch am 3.3., mündliche Zustimmung',
    hinweis: bearbeitenModus
      ? 'Eine bereits erfasste Einwilligung bleibt bestehen und wird hier nicht überschrieben. Ist noch keine hinterlegt, kann sie hier nachgetragen werden.'
      : 'Bleibt das Feld leer, wird der Empfänger gespeichert, aber von keiner Aussendung angeschrieben. Ohne dokumentierte Einwilligung ist Werbeversand nicht zulässig.',
  });

  const meldungsBereich = el('div');

  const speichern = async () => {
    const email = emailEingabe.value.trim();
    if (!email || !email.includes('@')) {
      ersetzen(meldungsBereich, meldung('warnung', 'Bitte eine gültige E-Mail-Adresse angeben.'));
      return;
    }

    const eingabe = {
      email,
      ...(anredeEingabe.value ? { anredeForm: anredeEingabe.value } : {}),
      ...(vornameEingabe.value.trim() ? { vorname: vornameEingabe.value.trim() } : {}),
      ...(nachnameEingabe.value.trim() ? { nachname: nachnameEingabe.value.trim() } : {}),
      ...(titelVorEingabe.value.trim() ? { titelVor: titelVorEingabe.value.trim() } : {}),
      ...(titelNachEingabe.value.trim() ? { titelNach: titelNachEingabe.value.trim() } : {}),
      ...(firmaEingabe.value.trim() ? { firma: firmaEingabe.value.trim() } : {}),
      ...(einwilligungEingabe.value.trim()
        ? { einwilligungsQuelle: einwilligungEingabe.value.trim() }
        : {}),
    };

    ersetzen(meldungsBereich, laedt(bearbeitenModus ? 'Wird gespeichert …' : 'Wird angelegt …'));
    try {
      if (bearbeitenModus) {
        await Kontakte.aktualisieren(kontakt.id, eingabe);
        schliessen();
        await nachSpeichern();
        return;
      }

      const ergebnis = await Kontakte.anlegen(eingabe);
      if (!ergebnis.angeschriebenWird) {
        // Dialog bewusst offen lassen: diese Warnung ist der wichtigste Satz
        // im ganzen Vorgang und darf nicht mit einem automatischen Schließen
        // verschwinden, bevor sie gelesen wurde.
        ersetzen(
          meldungsBereich,
          meldung(
            'warnung',
            'Angelegt — aber ohne Angabe einer Einwilligung wird dieser Empfänger von keiner Aussendung angeschrieben.',
          ),
        );
        await nachSpeichern(email);
        return;
      }
      schliessen();
      await nachSpeichern(email);
    } catch (fehler) {
      ersetzen(
        meldungsBereich,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Speichern fehlgeschlagen'),
      );
    }
  };

  const schliessen = dialog({
    titel: bearbeitenModus ? 'Empfänger bearbeiten' : 'Empfänger anlegen',
    inhalt: [
      emailFeld,
      anredeFeld,
      el('div.feld-reihe', [titelVorFeld, vornameFeld]),
      el('div.feld-reihe', [nachnameFeld, titelNachFeld]),
      firmaFeld,
      einwilligungFeld,
      meldungsBereich,
    ],
    aktionen: [
      knopf('Abbrechen', { beiKlick: () => schliessen() }),
      knopf(bearbeitenModus ? 'Speichern' : 'Anlegen', { art: 'haupt', beiKlick: speichern }),
    ],
  });
}

/**
 * Rückfrage vor dem Sperren — danach wird der Empfänger von keiner
 * Aussendung mehr angeschrieben, auch nicht nach einem erneuten Listenimport.
 *
 * @param {any} kontakt
 * @param {() => Promise<void>} neuLaden
 */
function empfaengerSperrenDialog(kontakt, neuLaden) {
  const meldungsBereich = el('div');

  const sperrenKnopf = knopf('Sperren', {
    art: 'gefahr',
    beiKlick: async () => {
      sperrenKnopf.disabled = true;
      ersetzen(meldungsBereich, laedt('Wird gesperrt …'));
      try {
        await Kontakte.sperren(kontakt.id);
        schliessen();
        await neuLaden();
      } catch (fehler) {
        sperrenKnopf.disabled = false;
        ersetzen(
          meldungsBereich,
          meldung('fehler', fehler instanceof Error ? fehler.message : 'Sperren fehlgeschlagen'),
        );
      }
    },
  });

  const schliessen = dialog({
    titel: 'Empfänger sperren',
    inhalt: [
      el('p', { text: `Soll ${kontakt.email} für alle künftigen Aussendungen gesperrt werden?` }),
      el('p.feld-hinweis', {
        text: 'Der Empfänger bleibt erhalten, wird aber von keiner Aussendung mehr angeschrieben — auch nicht nach einem erneuten Listenimport. Über „Entsperren" lässt sich das rückgängig machen.',
      }),
      meldungsBereich,
    ],
    aktionen: [knopf('Abbrechen', { beiKlick: () => schliessen() }), sperrenKnopf],
  });
}

/**
 * Löscht ohne weitere Rückfrage nur, was direkt bestätigt wurde. Lässt den
 * Dialog nach Erfolg offen stehen, damit der von der Schnittstelle
 * zurückgegebene Hinweis wirklich gelesen werden kann.
 *
 * @param {any} kontakt
 * @param {() => Promise<void>} neuLaden
 */
function empfaengerLoeschenDialog(kontakt, neuLaden) {
  const meldungsBereich = el('div');
  const bezeichnung = [kontakt.vorname, kontakt.nachname].filter(Boolean).join(' ') || kontakt.email;

  const loeschenKnopf = knopf('Löschen', {
    art: 'gefahr',
    beiKlick: async () => {
      loeschenKnopf.disabled = true;
      ersetzen(meldungsBereich, laedt('Wird gelöscht …'));
      try {
        const ergebnis = await Kontakte.loeschen(kontakt.id);
        await neuLaden();
        ersetzen(
          meldungsBereich,
          meldung('erfolg', 'Der Empfänger wurde gelöscht.'),
          el('p.feld-hinweis', { text: ergebnis.hinweis }),
        );
        loeschenKnopf.remove();
        abbrechenKnopf.textContent = 'Schließen';
      } catch (fehler) {
        loeschenKnopf.disabled = false;
        ersetzen(
          meldungsBereich,
          meldung('fehler', fehler instanceof Error ? fehler.message : 'Löschen fehlgeschlagen'),
        );
      }
    },
  });

  const abbrechenKnopf = knopf('Abbrechen', { beiKlick: () => schliessen() });

  const schliessen = dialog({
    titel: 'Empfänger löschen',
    inhalt: [
      el('p', { text: `Soll „${bezeichnung}" (${kontakt.email}) gelöscht werden?` }),
      el('p.feld-hinweis', {
        text: 'Die Adresse bleibt danach gesperrt, damit ein späterer Listenimport sie nicht erneut anschreibt.',
      }),
      meldungsBereich,
    ],
    aktionen: [abbrechenKnopf, loeschenKnopf],
  });
}

/**
 * @param {() => void} neuLaden
 */
function importAssistent(neuLaden) {
  const dateiFeld = /** @type {HTMLInputElement} */ (
    el('input', { type: 'file', accept: '.csv,.txt,text/csv' })
  );

  const { feld: quelleFeld, eingabe: quelleEingabe } = eingabefeld({
    beschriftung: 'Woher stammt diese Liste?',
    platzhalter: 'z. B. Mandantenexport BMD, Stand Juli',
    hinweis: 'Wird gespeichert, damit später nachvollziehbar ist, woher ein Kontakt kam.',
  });

  const { feld: einwilligungFeld, eingabe: einwilligungEingabe } = eingabefeld({
    beschriftung: 'Auf welcher Grundlage dürfen diese Personen angeschrieben werden?',
    platzhalter: 'z. B. Anmeldeformular auf der Website',
    hinweis:
      'Bleibt das Feld leer, werden die Kontakte gespeichert, aber von keiner Aussendung angeschrieben. Ohne dokumentierte Einwilligung ist Werbeversand nicht zulässig.',
  });

  const ergebnisBereich = el('div');

  const einlesen = async () => {
    const datei = dateiFeld.files?.[0];
    if (!datei) {
      ersetzen(ergebnisBereich, meldung('warnung', 'Bitte zuerst eine Datei auswählen.'));
      return;
    }
    if (!quelleEingabe.value.trim()) {
      ersetzen(ergebnisBereich, meldung('warnung', 'Bitte die Herkunft der Liste angeben.'));
      return;
    }

    ersetzen(ergebnisBereich, laedt('Liste wird eingelesen …'));
    try {
      const csv = await datei.text();
      const ergebnis = await Kontakte.importieren({
        csv,
        quelle: quelleEingabe.value.trim(),
        ...(einwilligungEingabe.value.trim()
          ? { einwilligungsQuelle: einwilligungEingabe.value.trim() }
          : {}),
      });

      const teile = [
        meldung(
          'erfolg',
          `${ergebnis.neu} neu aufgenommen, ${ergebnis.aktualisiert} aktualisiert.` +
            (ergebnis.uebersprungen ? ` ${ergebnis.uebersprungen} Zeilen übersprungen.` : ''),
        ),
      ];

      // Eigene, deutliche Meldung: in „12 Zeilen übersprungen" geht unter,
      // dass davon 11 an der Domainsperre hängen geblieben sind - und wer das
      // nicht liest, sucht den Fehler in seiner Liste.
      if (ergebnis.testmodusUebersprungen) {
        teile.push(
          meldung(
            'warnung',
            `${ergebnis.testmodusUebersprungen} Adressen wurden nicht aufgenommen: ${ergebnis.testmodusHinweis}`,
          ),
        );
      }

      if (!einwilligungEingabe.value.trim()) {
        teile.push(
          meldung(
            'warnung',
            'Ohne Angabe einer Einwilligung werden diese Kontakte von keiner Aussendung angeschrieben.',
          ),
        );
      }

      if (ergebnis.unbekannteSpalten?.length) {
        teile.push(
          meldung(
            'info',
            `Zusätzliche Spalten übernommen: ${ergebnis.unbekannteSpalten.join(', ')}. Sie lassen sich in Vorlagen als Platzhalter verwenden, z. B. {{ ${ergebnis.unbekannteSpalten[0]} }}.`,
          ),
        );
      }

      if (ergebnis.probleme?.length) {
        teile.push(
          el('details.vertiefung', [
            el('summary', { text: `${ergebnis.probleme.length} Zeilen mit Problemen ansehen` }),
            el('div', [
              tabelle({
                spalten: [
                  { titel: 'Zeile', zelle: (p) => String(p.zeile), schmal: true },
                  { titel: 'Grund', zelle: (p) => p.grund },
                  { titel: 'Wert', zelle: (p) => p.wert || '—' },
                ],
                zeilen: ergebnis.probleme.slice(0, 50),
              }),
            ]),
          ]),
        );
      }

      ersetzen(ergebnisBereich, ...teile);
      neuLaden();
    } catch (fehler) {
      ersetzen(
        ergebnisBereich,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Import fehlgeschlagen'),
      );
    }
  };

  dialog({
    titel: 'Empfänger aus einer Liste hinzufügen',
    inhalt: [
      el('p', {
        text: 'Exportieren Sie Ihre Liste aus Excel als CSV. Semikolon als Trennzeichen wird erkannt — das ist die übliche deutsche Einstellung.',
      }),
      el('div.feld', [
        el('label', { text: 'Datei' }),
        dateiFeld,
        el('p.feld-hinweis', {
          text: 'Erkannte Spalten: E-Mail, Anrede, Titel, Vorname, Nachname, Firma. Weitere Spalten bleiben als Platzhalter nutzbar.',
        }),
      ]),
      quelleFeld,
      einwilligungFeld,
      ergebnisBereich,
    ],
    aktionen: [knopf('Liste einlesen', { art: 'haupt', beiKlick: einlesen })],
  });
}

/**
 * Sperrgründe in Worten. Der Unterschied zählt: eine eigene Abmeldung darf
 * nicht aufgehoben werden, eine Anbieterstörung schon.
 * @type {Record<string, string>}
 */
const SPERRGRUND_TEXT = {
  hardbounce: 'unzustellbar',
  beschwerde: 'Beschwerde',
  abgemeldet: 'abgemeldet',
  manuell: 'gesperrt',
};

/**
 * @returns {import('../app.js').Ansicht}
 */
/**
 * Anmeldedialog für das Freischalten des Testbetriebs.
 *
 * Bewusst eine eigene Anmeldung statt eines Häkchens: das Zugangstoken kennt
 * jede Bearbeiterin, die den Editor bedient. Der Schritt, ab dem Nachrichten
 * an Fremde hinausgehen können, soll nicht derselbe Handgriff sein wie das
 * tägliche Anmelden.
 *
 * @param {object} optionen
 * @param {boolean} optionen.aktivieren true = Sperre wieder einschalten.
 * @param {string|null} optionen.domain
 * @param {() => Promise<void>} optionen.nachher
 */
function testmodusDialog({ aktivieren, domain, nachher }) {
  const meldungsBereich = el('div');

  const { feld: benutzerFeld, eingabe: benutzerEingabe } = eingabefeld({
    beschriftung: 'Administrator',
    platzhalter: 'name@steuerservice.at',
  });
  const { feld: passwortFeld, eingabe: passwortEingabe } = eingabefeld({
    beschriftung: 'Passwort',
    art: 'password',
  });

  const ausfuehren = async () => {
    ersetzen(meldungsBereich, laedt(aktivieren ? 'Wird gesperrt …' : 'Wird freigeschaltet …'));
    try {
      await Testmodus.freischalten({
        benutzer: benutzerEingabe.value.trim(),
        passwort: passwortEingabe.value,
        aktiv: aktivieren,
      });
      schliessen();
      await nachher();
    } catch (fehler) {
      ersetzen(
        meldungsBereich,
        meldung('warnung', fehler instanceof Error ? fehler.message : 'Freischalten fehlgeschlagen'),
      );
    }
  };

  // Enter im Passwortfeld löst aus — sonst sucht man nach dem Knopf.
  passwortEingabe.addEventListener('keydown', (ereignis) => {
    if (/** @type {KeyboardEvent} */ (ereignis).key === 'Enter') ausfuehren();
  });

  const schliessen = dialog({
    titel: aktivieren ? 'Testbetrieb wieder einschalten' : 'Testbetrieb freischalten',
    inhalt: [
      el('p', {
        text: aktivieren
          ? `Danach werden wieder ausschließlich Adressen auf @${domain} aufgenommen. Bereits erfasste Empfänger bleiben erhalten.`
          : `Danach lassen sich Empfänger jeder Domain erfassen — nicht nur @${domain}. Ab diesem Zeitpunkt können Aussendungen echte Mandanten erreichen.`,
      }),
      el('p.feld-hinweis', {
        text: 'Diese Anmeldung ist nicht das Zugangstoken der Oberfläche, sondern der getrennte Administratorzugang aus der Serverkonfiguration.',
      }),
      benutzerFeld,
      passwortFeld,
      meldungsBereich,
    ],
    aktionen: [
      knopf('Abbrechen', { art: 'still', beiKlick: () => schliessen() }),
      knopf(aktivieren ? 'Wieder sperren' : 'Freischalten', {
        art: 'haupt',
        beiKlick: ausfuehren,
      }),
    ],
  });

  benutzerEingabe.focus();
}

/**
 * Empfängergruppen anlegen und pflegen.
 *
 * Eine Aussendung geht selten an alle: die Fristenerinnerung betrifft
 * Unternehmer, die Einladung zum Webinar nur die Region. Die Gruppen waren im
 * Werkzeug bereits vorgesehen und in der Aussendung auswählbar — es fehlte
 * jeder Weg, welche anzulegen. Damit gab es dauerhaft genau eine Auswahl:
 * „Alle Empfänger".
 *
 * Zugeordnet wird über die Suche, nicht über Häkchen: bei mehreren hundert
 * Mandanten ist Anklicken keine Arbeitsweise. „Alle Treffer zu 'GmbH'" ist
 * einer.
 *
 * @param {() => Promise<void>|void} neuLaden
 */
function gruppenDialog(neuLaden) {
  const listenBereich = el('div');
  const meldungsBereich = el('div');

  const { feld: nameFeld, eingabe: nameEingabe } = eingabefeld({
    beschriftung: 'Name der Gruppe',
    platzhalter: 'z. B. Unternehmer',
  });
  const { feld: sucheFeld, eingabe: sucheEingabe } = eingabefeld({
    beschriftung: 'Wer soll hinein? (optional)',
    platzhalter: 'Suchbegriff — z. B. GmbH',
    hinweis:
      'Alle Empfänger, auf die der Begriff passt (Name, E-Mail oder Firma), kommen in die Gruppe. ' +
      'Ohne Angabe entsteht eine leere Gruppe, die Sie später füllen.',
  });

  const laden = async () => {
    ersetzen(listenBereich, laedt());
    try {
      const gruppen = await Segmente.liste();
      ersetzen(
        listenBereich,
        gruppen.length
          ? tabelle({
              spalten: [
                { titel: 'Gruppe', zelle: (g) => g.name },
                { titel: 'Empfänger', zelle: (g) => String(g.anzahl ?? 0), schmal: true },
                {
                  titel: 'Aktionen',
                  zelle: (g) =>
                    el('div.knopf-reihe', [
                      knopf('Empfänger ergänzen', {
                        art: 'still',
                        titel: 'Weitere Empfänger über einen Suchbegriff hinzufügen',
                        beiKlick: () => ergaenzen(g),
                      }),
                      knopf('Löschen', {
                        art: 'still',
                        titel: 'Nur die Gruppe entfernen — die Empfänger bleiben erhalten',
                        beiKlick: () => gruppeLoeschen(g),
                      }),
                    ]),
                },
              ],
              zeilen: gruppen,
              leerText: 'Noch keine Gruppen. Legen Sie unten die erste an.',
            })
          : meldung(
              'info',
              'Noch keine Gruppen vorhanden. Ohne Gruppen geht jede Aussendung an alle Empfänger.',
            ),
      );
    } catch (fehler) {
      ersetzen(
        listenBereich,
        meldung('warnung', fehler instanceof Error ? fehler.message : 'Gruppen nicht ladbar'),
      );
    }
  };

  /** @param {any} gruppe */
  const ergaenzen = async (gruppe) => {
    const begriff = window.prompt(
      `Welche Empfänger sollen zu „${gruppe.name}" dazukommen?\n\n` +
        'Suchbegriff für Name, E-Mail oder Firma:',
    );
    if (!begriff?.trim()) return;
    ersetzen(meldungsBereich, laedt('Empfänger werden zugeordnet …'));
    try {
      const ergebnis = await Segmente.kontakteHinzufuegen(gruppe.id, { suche: begriff.trim() });
      ersetzen(
        meldungsBereich,
        meldung(
          'erfolg',
          `${ergebnis.hinzugefuegt} von ${ergebnis.geprueft} Treffern zu „${gruppe.name}" hinzugefügt` +
            `${ergebnis.geprueft > ergebnis.hinzugefuegt ? ' — die übrigen waren bereits darin.' : '.'}`,
        ),
      );
      await laden();
      await neuLaden();
    } catch (fehler) {
      ersetzen(
        meldungsBereich,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Zuordnen fehlgeschlagen'),
      );
    }
  };

  /** @param {any} gruppe */
  const gruppeLoeschen = async (gruppe) => {
    const sicher = window.confirm(
      `Gruppe „${gruppe.name}" löschen?\n\n` +
        'Die Empfänger bleiben erhalten — nur die Zusammenstellung wird entfernt.',
    );
    if (!sicher) return;
    try {
      await Segmente.loeschen(gruppe.id);
      ersetzen(meldungsBereich, meldung('erfolg', `Gruppe „${gruppe.name}" gelöscht.`));
      await laden();
      await neuLaden();
    } catch (fehler) {
      ersetzen(
        meldungsBereich,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Löschen fehlgeschlagen'),
      );
    }
  };

  const anlegen = async () => {
    const name = nameEingabe.value.trim();
    if (!name) {
      ersetzen(meldungsBereich, meldung('warnung', 'Bitte einen Namen für die Gruppe angeben.'));
      return;
    }
    ersetzen(meldungsBereich, laedt('Gruppe wird angelegt …'));
    try {
      const neu = await Segmente.anlegen({ name });
      const suche = sucheEingabe.value.trim();
      if (suche) {
        const ergebnis = await Segmente.kontakteHinzufuegen(neu.id, { suche });
        ersetzen(
          meldungsBereich,
          meldung(
            'erfolg',
            `Gruppe „${name}" angelegt mit ${ergebnis.hinzugefuegt} Empfängern.`,
          ),
        );
      } else {
        ersetzen(
          meldungsBereich,
          meldung('erfolg', `Gruppe „${name}" angelegt. Sie ist noch leer.`),
        );
      }
      nameEingabe.value = '';
      sucheEingabe.value = '';
      await laden();
      await neuLaden();
    } catch (fehler) {
      const text = fehler instanceof Error ? fehler.message : 'Anlegen fehlgeschlagen';
      ersetzen(
        meldungsBereich,
        meldung(
          'fehler',
          /UNIQUE|unique/.test(text) ? `Eine Gruppe „${name}" gibt es bereits.` : text,
        ),
      );
    }
  };

  const schliessen = dialog({
    titel: 'Empfängergruppen',
    breit: true,
    inhalt: [
      el('p', {
        text: 'Gruppen bestimmen, wer eine Aussendung bekommt. Bei der Aussendung wählen Sie dann statt „Alle Empfänger" eine Gruppe.',
      }),
      listenBereich,
      el('hr'),
      el('h3', { text: 'Neue Gruppe' }),
      nameFeld,
      sucheFeld,
      el('div.knopf-reihe', [knopf('Gruppe anlegen', { art: 'haupt', beiKlick: anlegen })]),
      meldungsBereich,
    ],
    aktionen: [knopf('Schließen', { beiKlick: () => schliessen() })],
  });

  laden();
}

export function kontakteAnsicht() {
  return {
    titel: 'Empfänger',
    async zeichnen(behaelter) {
      let suchtext = '';

      const listenBereich = el('div');
      // Steht über der Liste, nicht in einem Aufklappbereich: wer Empfänger
      // erfasst, muss VORHER wissen, dass gerade nur die eigene Domain
      // angenommen wird — sonst erfährt er es erst an der Fehlermeldung.
      const testmodusBereich = el('div');
      // Für kurzlebige Rückmeldungen zu Zeilenaktionen (z. B. Entsperren),
      // die keine eigene Rückfrage brauchen und deshalb keinen Dialog
      // bekommen — bleibt beim Neuladen der Liste stehen, weil listeLaden()
      // nur listenBereich ersetzt.
      const aktionsMeldungBereich = el('div');

      /** Wie viele Einträge eine Runde umfasst. */
      const SEITENGROESSE = 200;

      /** Bereits geladene Einträge — beim Nachladen wächst diese Liste. */
      let geladene = /** @type {any[]} */ ([]);

      /**
       * Lädt die Liste.
       *
       * Ohne das Nachladen endete die Empfängerverwaltung bei den ersten 200
       * Einträgen: bei 921 Empfängern waren über 700 davon über die
       * Oberfläche schlicht nicht erreichbar — man musste wissen, wonach man
       * sucht, um sie überhaupt zu Gesicht zu bekommen.
       *
       * @param {{ weitere?: boolean, vonVorn?: boolean }} [optionen]
       */
      const listeLaden = async (optionen = {}) => {
        const weitere = optionen.weitere === true;

        // Nach einer Zeilenaktion (sperren, löschen, bearbeiten) wird die Liste
        // neu geladen. Ohne diesen Merker stünde man danach wieder bei den
        // ersten 200 — wer bei Eintrag 800 etwas gesperrt hat, müsste sich
        // viermal neu durchklicken. Die Schnittstelle gibt höchstens 1000
        // Einträge auf einmal heraus.
        // Bei einer neuen Suche gilt das nicht: dort will man die erste Seite
        // der Treffer sehen, nicht so viele Zeilen wie vorher offen waren.
        const behalten = optionen.vonVorn
          ? SEITENGROESSE
          : Math.min(Math.max(geladene.length, SEITENGROESSE), 1000);

        if (!weitere) {
          geladene = [];
          ersetzen(listenBereich, laedt());
        }
        const daten = await Kontakte.liste({
          suche: suchtext,
          limit: weitere ? SEITENGROESSE : behalten,
          offset: weitere ? geladene.length : 0,
        });
        geladene = weitere ? [...geladene, ...daten.kontakte] : daten.kontakte;
        daten.kontakte = geladene;

        const inhalt = tabelle({
          spalten: [
            {
              titel: 'Anrede in der Mail',
              zelle: (k) =>
                el('span', [
                  el('span', { text: anredeVorschau(k) }),
                  // Wer nicht mehr angeschrieben wird, muss das hier sehen —
                  // sonst sucht jemand den Fehler in der Aussendung, während
                  // der Grund beim Empfänger liegt. Der Sperrgrund steht dabei:
                  // eine eigene Abmeldung ist etwas anderes als eine
                  // unzustellbare Adresse.
                  k.abgemeldetAm
                    ? el('span', { style: 'display:block' }, [
                        kennzeichen('abgemeldet', 'rot'),
                      ])
                    : k.gesperrt
                      ? el('span', { style: 'display:block' }, [
                          kennzeichen(SPERRGRUND_TEXT[k.sperrgrund] ?? 'gesperrt', 'rot'),
                        ])
                      : !k.anredeForm || !k.nachname
                        ? el('span', { style: 'display:block' }, [
                            kennzeichen('unpersönlich', 'gelb'),
                          ])
                        : null,
                ]),
            },
            { titel: 'E-Mail', zelle: (k) => k.email },
            { titel: 'Firma', zelle: (k) => k.firma || '—' },
            {
              titel: 'Anrede',
              zelle: (k) => ANREDE_TEXT[/** @type {keyof typeof ANREDE_TEXT} */ (k.anredeForm)] ?? '—',
              schmal: true,
            },
            {
              titel: 'Aktionen',
              zelle: (k) =>
                el('div.knopf-reihe', [
                  knopf('Bearbeiten', {
                    art: 'still',
                    titel: 'Angaben dieses Empfängers ändern',
                    beiKlick: () =>
                      empfaengerFormular({
                        kontakt: k,
                        nachSpeichern: async () => listeLaden(),
                      }),
                  }),
                  // Entweder sperren ODER entsperren — nie beides. Ein Knopf,
                  // der bei diesem Empfänger nichts bewirken kann, ist kein
                  // Angebot, sondern eine Einladung zum Fehlgriff.
                  k.gesperrt
                    ? knopf('Entsperren', {
                        art: 'still',
                        titel: 'Sperre aufheben, sofern keine eigene Abmeldung vorliegt',
                        beiKlick: () => entsperrenAusfuehren(k),
                      })
                    : knopf('Sperren', {
                        art: 'still',
                        titel: 'Von allen künftigen Aussendungen ausschließen',
                        beiKlick: () => empfaengerSperrenDialog(k, listeLaden),
                      }),
                  knopf('Löschen', {
                    art: 'still',
                    titel: 'Diesen Empfänger entfernen',
                    beiKlick: () => empfaengerLoeschenDialog(k, listeLaden),
                  }),
                ]),
            },
          ],
          zeilen: daten.kontakte,
          leerText: suchtext
            ? 'Zu dieser Suche gibt es keine Treffer.'
            : 'Noch keine Empfänger vorhanden. Legen Sie einzeln an oder laden Sie eine Liste aus Excel hoch.',
        });

        const ohneAnrede = daten.kontakte.filter(
          (/** @type {any} */ k) => !k.anredeForm || !k.nachname,
        ).length;

        const gesperrteAngezeigt = daten.kontakte.filter((/** @type {any} */ k) => k.gesperrt).length;

        // `gesamt` zählt bei einer Suche die Treffer, sonst den ganzen Bestand;
        // `gesamtbestand` ist immer der ganze Bestand.
        const bestandsText = suchtext
          ? `${daten.kontakte.length} von ${daten.gesamt} Treffern für „${suchtext}" (${daten.gesamtbestand ?? daten.gesamt} Empfänger im Bestand).`
          : `${daten.kontakte.length} von ${daten.gesamt} Empfängern angezeigt.`;

        const nochOffen = Math.max(daten.gesamt - daten.kontakte.length, 0);

        ersetzen(
          listenBereich,
          el('div', [
            el('p.feld-hinweis', { text: bestandsText }),
            ohneAnrede
              ? meldung(
                  'info',
                  `${ohneAnrede} Empfänger bekommen „Sehr geehrte Damen und Herren", weil Anrede oder Nachname fehlen. Über „Bearbeiten" lässt sich das für eine persönliche Ansprache ergänzen.`,
                )
              : null,
            inhalt,
            // Ohne diesen Knopf endet der Bestand für die Bedienerin nach der
            // ersten Seite — der Rest ist nur über die Suche erreichbar.
            nochOffen
              ? el('div.knopf-reihe', { style: 'margin-top:1rem;justify-content:center' }, [
                  knopf(`Weitere ${Math.min(nochOffen, SEITENGROESSE)} laden`, {
                    titel: `Es sind noch ${nochOffen} weitere vorhanden`,
                    beiKlick: () => listeLaden({ weitere: true }),
                  }),
                ])
              : null,
          ]),
        );

        // Auf schmalen Bildschirmen werden Tabellen zu Karten - dafür braucht
        // jede Zelle die Spaltenüberschrift als Beschriftung.
        const koepfe = ['Anrede in der Mail', 'E-Mail', 'Firma', 'Anrede', 'Aktionen'];
        listenBereich.querySelectorAll('tbody tr').forEach((zeile) => {
          zeile.querySelectorAll('td').forEach((zelle, index) => {
            zelle.setAttribute('data-spalte', koepfe[index] ?? '');
          });
        });
      };

      /**
       * Entsperren braucht keine Rückfrage — anders als Sperren und Löschen
       * ist es eine rein wiederherstellende Aktion. Die Rückmeldung (auch der
       * Sonderfall „das war eine echte Abmeldung, kein Fehler") landet knapp
       * über der Liste, statt in einem weiteren Dialog.
       * @param {any} kontakt
       */
      const entsperrenAusfuehren = async (kontakt) => {
        ersetzen(aktionsMeldungBereich, laedt('Wird entsperrt …'));
        try {
          await Kontakte.entsperren(kontakt.id);
          ersetzen(
            aktionsMeldungBereich,
            meldung('erfolg', `${kontakt.email} ist wieder für Aussendungen freigegeben.`),
          );
          await listeLaden();
        } catch (fehler) {
          ersetzen(
            aktionsMeldungBereich,
            meldung('warnung', fehler instanceof Error ? fehler.message : 'Entsperren fehlgeschlagen'),
          );
        }
      };

      /**
       * Zeigt den Stand des Testbetriebs.
       *
       * Auch der freigeschaltete Zustand wird angezeigt, nicht nur der
       * gesperrte: eine offene Sperre, die niemand sieht, ist der Fall, in dem
       * eine Übung bei echten Mandanten landet.
       */
      const testmodusLaden = async () => {
        try {
          const stand = await Testmodus.stand();
          if (!stand.eingerichtet) {
            ersetzen(testmodusBereich, el('div'));
            return;
          }

          if (stand.aktiv) {
            ersetzen(
              testmodusBereich,
              meldung(
                'info',
                stand.hinweis,
                stand.adminMoeglich
                  ? knopf('Freischalten', {
                      art: 'still',
                      titel: 'Beschränkung auf die eigene Domain aufheben (Administratoranmeldung)',
                      beiKlick: () =>
                        testmodusDialog({
                          aktivieren: false,
                          domain: stand.domain,
                          nachher: async () => {
                            await testmodusLaden();
                            await listeLaden();
                          },
                        }),
                    })
                  : null,
              ),
            );
            return;
          }

          const wer = stand.freigeschaltetVon ? ` von ${stand.freigeschaltetVon}` : '';
          const wann = stand.freigeschaltetAm ? ` am ${zeitpunkt(stand.freigeschaltetAm)}` : '';
          ersetzen(
            testmodusBereich,
            meldung(
              'warnung',
              `Der Testbetrieb ist freigeschaltet${wer}${wann}. Es lassen sich Empfänger jeder Domain erfassen — Aussendungen können echte Mandanten erreichen.`,
              knopf('Wieder sperren', {
                art: 'still',
                titel: `Nur noch Adressen auf @${stand.domain} aufnehmen`,
                beiKlick: () =>
                  testmodusDialog({
                    aktivieren: true,
                    domain: stand.domain,
                    nachher: async () => {
                      await testmodusLaden();
                      await listeLaden();
                    },
                  }),
              }),
            ),
          );
        } catch {
          // Der Hinweis ist Beiwerk. Fällt er aus, muss die Empfängerliste
          // trotzdem stehen - die Sperre selbst sitzt ohnehin im Server.
          ersetzen(testmodusBereich, el('div'));
        }
      };

      const { feld: sucheFeld, eingabe: sucheEingabe } = eingabefeld({
        beschriftung: 'Suchen',
        platzhalter: 'Name, E-Mail oder Firma',
      });

      let sucheZeitgeber;
      sucheEingabe.addEventListener('input', () => {
        clearTimeout(sucheZeitgeber);
        // Kurz warten, damit nicht jeder Tastendruck eine Anfrage auslöst.
        sucheZeitgeber = setTimeout(() => {
          suchtext = sucheEingabe.value.trim();
          listeLaden({ vonVorn: true });
        }, 250);
      });

      /**
       * Nach dem Neuanlegen direkt auf den neuen Empfänger suchen: bei 920
       * Empfängern liegt er als jüngster Eintrag sonst außerhalb der ersten
       * angezeigten Seite, und ohne diesen Griff wäre nicht zu sehen, ob das
       * Anlegen wirklich funktioniert hat.
       * @param {string} [email]
       */
      const nachNeuanlage = async (email) => {
        if (email) {
          suchtext = email;
          sucheEingabe.value = email;
        }
        await listeLaden({ vonVorn: true });
      };

      ersetzen(
        behaelter,
        el('div.bahn', [
          el('div.seiten-kopf', [
            el('div', [
              el('h1', { text: 'Empfänger' }),
              el('p', {
                text: 'Wer den Newsletter bekommt — und wie diese Person angesprochen wird.',
              }),
            ]),
            el('div.knopf-reihe', [
              // Gruppen stehen neben dem Import: beides gehört zur Pflege des
              // Bestands, und ohne diesen Weg gab es dauerhaft nur „Alle
              // Empfänger" in der Aussendung.
              knopf('Gruppen', {
                titel: 'Empfängergruppen anlegen und pflegen',
                beiKlick: () => gruppenDialog(() => listeLaden({ vonVorn: true })),
              }),
              knopf('Liste hinzufügen', {
                beiKlick: () => importAssistent(listeLaden),
              }),
              // Primäre Aktion: wer beim Erstgespräch um den Newsletter bittet,
              // soll ohne den Umweg über eine CSV-Datei eintragbar sein.
              knopf('Empfänger anlegen', {
                art: 'haupt',
                beiKlick: () => empfaengerFormular({ nachSpeichern: nachNeuanlage }),
              }),
            ]),
          ]),
          testmodusBereich,
          karte({ inhalt: [sucheFeld, aktionsMeldungBereich, listenBereich] }),
        ]),
      );

      await testmodusLaden();
      await listeLaden();
    },
  };
}
